import { motion } from "framer-motion";
import { ArrowDown, Users, DollarSign, Shield, ChevronRight } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/29770731/pexels-photo-29770731.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A0F1C]/75" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24">
        <div className="max-w-4xl">
          {/* Overline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-sm uppercase tracking-[0.25em] font-bold text-[#e62e37] mb-6"
          >
            Experts en toiture au Québec
          </motion.p>

          {/* Main heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter text-white leading-[0.95] mb-8"
          >
            L'excellence en
            <br />
            <span className="text-[#0077cc]">toiture</span> &
            <br />
            imperméabilisation
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-base md:text-lg text-white/70 max-w-xl leading-relaxed mb-10"
          >
            Toitures & Imperméabilisations TPL inc. dessert les secteurs commercial,
            industriel, institutionnel, de la santé et résidentiel partout au Québec.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex flex-wrap gap-4 mb-16"
          >
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollTo("#contact")}
              className="flex items-center gap-2 bg-[#e62e37] text-white font-bold uppercase tracking-wide px-8 py-4 text-sm hover:bg-[#cc222b] transition-all duration-300"
            >
              Obtenir une soumission
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-portfolio-button"
              onClick={() => scrollTo("#realisations")}
              className="flex items-center gap-2 border-2 border-white/30 text-white font-bold uppercase tracking-wide px-8 py-4 text-sm hover:bg-white hover:text-[#0A0F1C] transition-all duration-300"
            >
              Nos réalisations
            </button>
          </motion.div>

          {/* Metric badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="flex flex-wrap gap-4"
          >
            <div data-testid="metric-employees" className="metric-badge px-6 py-4 flex items-center gap-3">
              <Users className="w-5 h-5 text-[#0077cc]" />
              <div>
                <p className="text-white font-bold text-lg leading-none">10-50</p>
                <p className="text-white/50 text-xs uppercase tracking-wider mt-1">Employés</p>
              </div>
            </div>
            <div data-testid="metric-contracts" className="metric-badge px-6 py-4 flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-[#0077cc]" />
              <div>
                <p className="text-white font-bold text-lg leading-none">1M$+</p>
                <p className="text-white/50 text-xs uppercase tracking-wider mt-1">Contrats</p>
              </div>
            </div>
            <div data-testid="metric-sectors" className="metric-badge px-6 py-4 flex items-center gap-3">
              <Shield className="w-5 h-5 text-[#0077cc]" />
              <div>
                <p className="text-white font-bold text-lg leading-none">5</p>
                <p className="text-white/50 text-xs uppercase tracking-wider mt-1">Secteurs</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <button
          onClick={() => scrollTo("#a-propos")}
          data-testid="hero-scroll-down"
          className="text-white/40 hover:text-white transition-colors"
        >
          <ArrowDown className="w-6 h-6 animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
}
